/**
 * @file PrivateSettings.js
 * @author Simon Tenedero
 * @created 2025-08-01
 * @lastModified 2025-08-01
 * @description file containing PrivateSettings component
 */
import { React, useRef, useState, useEffect } from "react";
import {
  updatePrivateChatName,
  updatePrivateChatUrl,
  updatePrivateChatIcon,
  removePrivateChatMember,
} from "../../utils/privateChatUtils";
import InviteUsersToPrivateChatModal from "../Modal/InviteUsersToPrivateChatModal";
import { getAuth } from "firebase/auth";

/**
 * Private chat settings component.
 * @param {Object} props - The component properties.
 * @param {Object} props.selectedPrivateChat - The selected private chat.
 * @param {Array} props.selectedPrivateChatMembers - Array of private chat members.
 * @param {Function} props.onClose - Callback function to close the settings.
 * @param {Boolean} props.loadingChatSettings - State variable tracking if chat settings are loading.
 *
 * @returns {JSX.Element} The PrivateSettings component.
 */
const PrivateSettings = ({
  selectedPrivateChat,
  selectedPrivateChatMembers,
  selectedPrivateChatPendingMembers,
  onClose,
  loadingChatSettings,
}) => {
  const [formData, setFormData] = useState({
    //use state initialization only happens once, not on re-renders
    chatName: "",
    videoUrl: "",
    chatIcon: null,
  });

  // State for success messages
  const [successMessages, setSuccessMessages] = useState({
    chatName: "",
    videoUrl: "",
    chatIcon: "",
  });

  const [inviteUsersPanelOpen, setInviteUsersPanelOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  const owner = selectedPrivateChatMembers.find(
    (m) => m.membershipStatus === "owner",
  );

  // Sync formData when props change
  useEffect(() => {
    console.log("Updating formData from props");
    console.log("Selected Private Chat:", selectedPrivateChat);
    console.log("Selected Private Chat Members:", selectedPrivateChatMembers);

    setFormData({
      chatName: selectedPrivateChat?.name || "",
      videoUrl: selectedPrivateChat?.url || "",
      chatIcon: selectedPrivateChat?.icon || null,
      members: selectedPrivateChatMembers || [],
    });
  }, [selectedPrivateChat, selectedPrivateChatMembers]); // Update when either prop changes

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear success message when user starts typing
    if (successMessages[name]) {
      setSuccessMessages((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle file change for chat icon
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          chatIcon: reader.result,
        }));
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
    }
  };

  // Handle clicking on the preview to select a file
  const handlePreviewClick = () => {
    fileInputRef.current?.click();
  };

  // Handle chat icon update
  const handleUpdateChatIcon = () => {
    updatePrivateChatIcon(selectedPrivateChat, selectedFile);

    // Show success message
    setSuccessMessages((prev) => ({
      ...prev,
      chatIcon: "Chat icon updated",
    }));

    // Clear message after 3 seconds
    setTimeout(() => {
      setSuccessMessages((prev) => ({
        ...prev,
        chatIcon: "",
      }));
    }, 3000);
  };

  // Handle removing the selected icon
  const handleRemoveIcon = () => {
    setFormData((prev) => ({
      ...prev,
      chatIcon: null,
    }));
    setSelectedFile(null);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle chat name update
  const handleUpdateChatName = () => {
    updatePrivateChatName(selectedPrivateChat, formData.chatName);

    // Show success message
    setSuccessMessages((prev) => ({
      ...prev,
      chatName: "Chat name updated",
    }));

    // Clear message after 3 seconds
    setTimeout(() => {
      setSuccessMessages((prev) => ({
        ...prev,
        chatName: "",
      }));
    }, 3000);
  };

  // Handle video URL update
  const handleUpdateVideoUrl = () => {
    console.log("Updating video URL:", formData.videoUrl);
    console.log("Selected Private Chat:", selectedPrivateChat);
    updatePrivateChatUrl(selectedPrivateChat, formData.videoUrl);

    // Show success message
    setSuccessMessages((prev) => ({
      ...prev,
      videoUrl: "Video URL updated",
    }));

    // Clear message after 3 seconds
    setTimeout(() => {
      setSuccessMessages((prev) => ({
        ...prev,
        videoUrl: "",
      }));
    }, 3000);
  };

  const handleRemoveMember = (memberId) => {
    removePrivateChatMember(memberId, selectedPrivateChat.id);
    console.log("Removing member:", memberId);
  };

  if (loadingChatSettings) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        {/* Loading spinner while settings are being loaded */}
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <span className="ml-2 text-lg text-base-content">
          Loading settings...
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col w-full h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between py-3 px-4 border-b border-base-content flex-shrink-0">
          <h2 className="text-lg font-semibold text-base-content">
            chat settings
          </h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-circle">
            <span className="text-lg">×</span>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-scroll">
          <div className="py-4 px-6 space-y-2">
            {/* Chat Icon Preview Section */}
            <div className="form-control">
              <div className="flex items-center justify-center mb-2">
                <div className="relative inline-block mt-2">
                  {formData.chatIcon ? (
                    <>
                      <img
                        src={formData.chatIcon}
                        alt="Chat Icon Preview"
                        onClick={handlePreviewClick}
                        className="w-20 h-20 rounded-full object-cover border-2 border-gray-300 shadow-sm cursor-pointer hover:border-primary hover:shadow-md transition-all"
                        title="Click to change icon"
                      />
                      <button
                        onClick={handleRemoveIcon}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors z-10"
                        title="Remove icon"
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <div
                      onClick={handlePreviewClick}
                      className="w-20 h-20 rounded-full bg-gray-300 border-2 border-gray-300 shadow-sm flex items-center justify-center cursor-pointer hover:border-primary hover:bg-gray-200 transition-all"
                      title="Click to select icon"
                    >
                      <span className="text-2xl font-bold text-gray-600">
                        {formData.chatName
                          ? formData.chatName.charAt(0).toUpperCase()
                          : "?"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* message telling user to select file */}
              <div className="text-xs text-gray-500 text-center mb-2">
                Click above to select icon
              </div>

              {/* Icon update button - only show if file is selected */}
              <div className="flex justify-center mb-2">
                <button
                  onClick={handleUpdateChatIcon}
                  className="btn btn-primary btn-sm"
                >
                  Update Icon
                </button>
              </div>

              {/* Success message for icon */}
              {successMessages.chatIcon && (
                <div className="text-sm text-success mt-1 text-center">
                  {successMessages.chatIcon}
                </div>
              )}
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {/* Chat Name */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">name</span>
              </label>
              <div className="flex">
                <input
                  type="text"
                  name="chatName"
                  value={formData.chatName}
                  onChange={handleInputChange}
                  placeholder="Enter chat name..."
                  className="input input-md input-bordered rounded-l-2xl rounded-r-none flex-1"
                  required
                />
                <button
                  onClick={handleUpdateChatName}
                  className="btn btn-primary rounded-l-none rounded-r-2xl w-[50px] border-l-0"
                  disabled={!formData.chatName.trim()}
                >
                  ✓
                </button>
              </div>
              {/* Success message */}
              {successMessages.chatName && (
                <div className="text-sm text-success mt-1 pl-1">
                  {successMessages.chatName}
                </div>
              )}
            </div>

            {/* Video URL */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">video URL</span>
              </label>
              <div className="flex">
                <input
                  type="text"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleInputChange}
                  placeholder="Enter video URL..."
                  className="input input-md input-bordered rounded-l-2xl rounded-r-none flex-1"
                  required
                />
                <button
                  onClick={handleUpdateVideoUrl}
                  className="btn btn-primary rounded-l-none rounded-r-2xl w-[50px] border-l-0"
                  disabled={!formData.videoUrl.trim()}
                >
                  ✓
                </button>
              </div>
              {/* Success message */}
              {successMessages.videoUrl && (
                <div className="text-sm text-success mt-1 pl-1">
                  {successMessages.videoUrl}
                </div>
              )}
            </div>

            {/* Members List with pending members - Fixed Height */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">
                  members ({selectedPrivateChatMembers.length})
                </span>
                <span className="label-text font-semibold">
                  {selectedPrivateChatPendingMembers?.length > 0 &&
                    `pending (${selectedPrivateChatPendingMembers.length})`}
                </span>
              </label>

              <div className="bg-base-100 rounded-lg overflow-hidden h-48 mb-3">
                {selectedPrivateChatMembers.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-base-content/50">
                    No members in this chat
                  </div>
                ) : (
                  <div className="h-full overflow-y-scroll p-3 space-y-2">
                    {[
                      ...selectedPrivateChatMembers,
                      ...selectedPrivateChatPendingMembers,
                    ].map((member, index) => (
                      <div
                        key={index}
                        className="flex items-center p-3 bg-base-200 rounded-lg transition-colors"
                      >
                        {/* Profile Photo */}
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-base-300 flex-shrink-0">
                          {member.profilePicture ? (
                            <img
                              src={member.profilePicture}
                              alt={member.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-primary flex items-center justify-center text-primary-content text-sm font-medium">
                              {member.name
                                ? member.name.charAt(0).toUpperCase()
                                : "U"}
                            </div>
                          )}
                        </div>

                        {/* Member Info */}
                        <div className="flex-1 min-w-0 ml-4">
                          <div className="font-medium text-base-content truncate">
                            {member.username || "Unknown User"}
                          </div>
                          {member.membershipStatus && (
                            <div className="text-sm text-base-content/70">
                              {member.membershipStatus}
                            </div>
                          )}
                        </div>

                        {/* Remove members, only render if owner*/}
                        {owner.uid === userId &&
                          member.membershipStatus === "member" && (
                            <div className="flex-shrink-0 ml-auto">
                              <button
                                onClick={() => handleRemoveMember(member.uid)}
                                className="btn btn-outline btn-error"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invite Button */}
              <button
                onClick={() => setInviteUsersPanelOpen(true)}
                className="btn btn-outline btn-primary w-full"
              >
                Invite Members
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Users Modal */}
      {inviteUsersPanelOpen && (
        <InviteUsersToPrivateChatModal
          selectedPrivateChat={selectedPrivateChat}
          onClose={() => setInviteUsersPanelOpen(false)}
          members={selectedPrivateChatMembers}
          selectedPrivateChatPendingMembers={selectedPrivateChatPendingMembers}
        />
      )}
    </>
  );
};

export default PrivateSettings;
